"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { openAuthorizationTracking } from "@/src/actions/prior-authorization-comms";
import { toast } from "@/src/hooks/use-toast";
import { invalidatePriorAuthorizationCommsCache } from "@/src/lib/data-fetchers/prior-authorization-comms-fetchers";
import { logger } from "@/src/lib/logging/logger";

export function useOpenAuthorizationTracking() {
  const queryClient = useQueryClient();
  const [isOpening, setIsOpening] = useState(false);

  const openAuthorizationTrackingSession = useCallback(
    async ({ authDisplayId, taskId }: { authDisplayId: string; taskId?: string }) => {
      setIsOpening(true);

      try {
        await openAuthorizationTracking({ authDisplayId, taskId });
        await invalidatePriorAuthorizationCommsCache(queryClient, authDisplayId);

        return true;
      } catch (err) {
        logger.error("Error opening authorization tracking", { error: err, authDisplayId, taskId });
        toast({
          title: "Could not open authorization",
          description: err instanceof Error ? err.message : "Could not open authorization tracking.",
          variant: "destructive",
        });

        return false;
      } finally {
        setIsOpening(false);
      }
    },
    [queryClient]
  );

  return { openAuthorizationTrackingSession, isOpening };
}
